import {
  type AddAttachmentDocumentInput,
  type AttachmentDocumentGateway,
  type AttachmentDocumentRecord,
  type UpdateAttachmentDocumentInput,
} from './attachmentDocuments';

/**
 * Demo-Speicher für Zusatzdokumente (im Arbeitsspeicher, wie die Maschinen-Anhänge). Bewusst hinter
 * dem async-Gateway, damit der Umzug auf die Supabase-Tabelle nur ein Austausch der Implementierung ist.
 * Inhalt (data:-URL) wird in Listen NICHT mitgeliefert -- nur bei getDocument.
 */

// Winziges, gültiges Beispiel-PDF (leere A4-Seite) für die Vorschau -- aus kurzem ASCII erzeugt,
// damit kein fragiler base64-Literal im Quelltext steht.
const SAMPLE_PDF_SOURCE = [
  '%PDF-1.4',
  '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
  '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
  '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]>>endobj',
  'trailer<</Root 1 0 R>>',
  '%%EOF',
].join('\n');
const SAMPLE_PDF = `data:application/pdf;base64,${btoa(SAMPLE_PDF_SOURCE)}`;

interface StoredDocument extends AttachmentDocumentRecord {
  readonly tenantId: string;
}

function toRecord(document: StoredDocument, content: string): AttachmentDocumentRecord {
  return {
    id: document.id,
    title: document.title,
    category: document.category,
    mimeType: document.mimeType,
    byteSize: document.byteSize,
    alwaysOffer: document.alwaysOffer,
    keywords: document.keywords,
    createdAt: document.createdAt,
    content,
  };
}

function seedFor(tenantId: string): StoredDocument[] {
  if (tenantId !== 'ftw') return [];
  const base = { tenantId, mimeType: 'application/pdf', byteSize: SAMPLE_PDF_SOURCE.length, content: SAMPLE_PDF, createdAt: '2026-07-21T08:00:00.000Z' };
  return [
    { ...base, id: 'ftw-doc-uvv', title: 'UVV-Prüfung Flurförderzeug', category: 'checklist', alwaysOffer: false, keywords: ['UVV', 'Durchsicht', 'Prüfung'] },
    { ...base, id: 'ftw-doc-ankunft', title: 'Maschinenankunftsprotokoll', category: 'protocol', alwaysOffer: true, keywords: [] },
    { ...base, id: 'ftw-doc-elektro', title: 'Elektro-Prüfung DGUV V3', category: 'checklist', alwaysOffer: false, keywords: ['Elektro', 'elektrisch', 'DGUV'] },
  ];
}

export class InMemoryAttachmentDocumentGateway implements AttachmentDocumentGateway {
  private readonly store = new Map<string, StoredDocument[]>();

  private documentsFor(tenantId: string): StoredDocument[] {
    let list = this.store.get(tenantId);
    if (!list) {
      list = seedFor(tenantId);
      this.store.set(tenantId, list);
    }
    return list;
  }

  async listDocuments(tenantId: string): Promise<readonly AttachmentDocumentRecord[]> {
    return this.documentsFor(tenantId).map((document) => toRecord(document, ''));
  }

  async getDocument(tenantId: string, documentId: string): Promise<AttachmentDocumentRecord> {
    const found = this.documentsFor(tenantId).find((document) => document.id === documentId);
    if (!found) throw new Error('Das Dokument wurde in diesem Mandanten nicht gefunden.');
    return toRecord(found, found.content);
  }

  async addDocument(input: AddAttachmentDocumentInput): Promise<AttachmentDocumentRecord> {
    const title = input.title.trim();
    if (!title) throw new Error('Das Dokument benötigt einen Namen.');
    if (!input.content) throw new Error('Es wurde keine Datei hochgeladen.');
    const record: StoredDocument = {
      tenantId: input.tenantId,
      id: `${input.tenantId}-doc-${crypto.randomUUID()}`,
      title,
      category: input.category,
      mimeType: input.mimeType || 'application/octet-stream',
      byteSize: input.byteSize,
      alwaysOffer: input.alwaysOffer,
      keywords: [...input.keywords],
      createdAt: new Date().toISOString(),
      content: input.content,
    };
    this.documentsFor(input.tenantId).unshift(record);
    return toRecord(record, '');
  }

  async updateDocument(input: UpdateAttachmentDocumentInput): Promise<AttachmentDocumentRecord> {
    const list = this.documentsFor(input.tenantId);
    const index = list.findIndex((document) => document.id === input.documentId);
    if (index < 0) throw new Error('Das Dokument wurde in diesem Mandanten nicht gefunden.');
    const title = input.title.trim();
    if (!title) throw new Error('Das Dokument benötigt einen Namen.');
    const updated: StoredDocument = {
      ...list[index],
      title,
      category: input.category,
      alwaysOffer: input.alwaysOffer,
      keywords: [...input.keywords],
    };
    list[index] = updated;
    return toRecord(updated, '');
  }

  async removeDocument(tenantId: string, documentId: string): Promise<void> {
    const list = this.documentsFor(tenantId);
    const index = list.findIndex((document) => document.id === documentId);
    if (index >= 0) list.splice(index, 1);
  }
}

export function createInMemoryAttachmentDocumentGateway(): AttachmentDocumentGateway {
  return new InMemoryAttachmentDocumentGateway();
}
